import React from 'react';
import { DSBadge, DSIcon } from '../design-system/primitives';

// Status and payment-method chips for membership.
//
// The wording comes from the server (services/membership/describe.js) so that a
// roster row, a queue row and a member's own record cannot disagree. This file
// owns only the visual mapping: which state gets which colour.
//
// Two rules from docs/MEMBERSHIP_SQUARE_UX_BRIEF.md section 5 are load-bearing:
//   - AT_RISK is a quiet flag, not an alarm. The member has full access, so it
//     is amber and never red.
//   - LAPSED and NEVER share one label in lists. A former member is a guest in
//     good standing, so neither reads as a failure.

const STATE_VISUAL = {
  ACTIVE: { variant: 'success', icon: 'user' },
  AT_RISK: { variant: 'warning', icon: 'warning' },
  ONBOARDING: { variant: 'info', icon: 'clock' },
  LAPSED: { variant: 'neutral', icon: 'gamepad' },
  NEVER: { variant: 'neutral', icon: 'gamepad' }
};

const METHOD_VISUAL = {
  SUBSCRIPTION: { icon: 'refresh', label: 'Subscription' },
  INVOICE: { icon: 'clipboard', label: 'Invoice' },
  CASH: { icon: 'card', label: 'Cash' },
  COMP: { icon: 'gift', label: 'Comp' },
  SPONSORED: { icon: 'users', label: 'Sponsored' }
};

// States that read as "not a member". Deliberately share one label: an admin
// scanning a list treats them the same, and the difference between "was a member
// until June" and "never joined" only matters once you are looking at them.
const INACTIVE_STATES = ['LAPSED', 'NEVER'];

const FALLBACK_LABEL = {
  ACTIVE: 'Member',
  AT_RISK: 'Attention',
  ONBOARDING: 'Onboarding',
  LAPSED: 'Not current',
  NEVER: 'Not current'
};

/**
 * The membership state chip.
 *
 * @param {object} props.status  rendered status from the API, when available
 * @param {string} props.state   raw state, used when only the column is to hand
 */
export function MembershipChip({ status, state, size = 'md', uppercase = false, withIcon = true, showInactive = false }) {
  const resolved = state || (status && status.state);
  if (!resolved) return null;

  // Most accounts are not members, so in a list "Not current" lands on nearly
  // every row and tells the reader nothing. Absence of a chip is the clearer
  // signal, and it leaves the chips that ARE there meaning something.
  //
  // The member's own record passes showInactive, because there the question is
  // about one person and the answer has to be stated.
  if (!showInactive && INACTIVE_STATES.includes(resolved)) return null;

  const visual = STATE_VISUAL[resolved] || STATE_VISUAL.NEVER;
  const label = (status && status.listLabel) || FALLBACK_LABEL[resolved] || 'Unknown';

  return (
    <DSBadge
      variant={visual.variant}
      size={size}
      uppercase={uppercase}
      leadingIcon={withIcon ? <DSIcon name={visual.icon} /> : undefined}
    >
      {label}
    </DSBadge>
  );
}

/**
 * How this person pays. New information: an admin previously had to open Square
 * to answer this (UX brief 4.2).
 *
 * HOW THEY PAY, NOT HOW THEY USED TO.
 *
 * `users.payment_method` is never cleared when a membership lapses - it is kept
 * deliberately, as the record of how they used to pay. So a lapsed member's row
 * carried an "Invoice" chip beside no membership chip at all, which reads as an
 * active billing arrangement for somebody who has none.
 *
 * `MembershipChip` already hides itself for LAPSED and NEVER; this is the same
 * guard, and the same decision the member's own record makes - see the Lauren
 * Russell note in `MembershipPanel`, where a non-member shows no payment plan.
 *
 * Callers pass `state` (or nothing, for the places where everyone shown is a
 * member). Passing nothing keeps the old behaviour rather than silently hiding
 * a chip somebody needs.
 */
export function PaymentMethodChip({ method, label, state, size = 'sm' }) {
  if (!method) return null;
  if (state && INACTIVE_STATES.includes(state)) return null;
  const visual = METHOD_VISUAL[method];
  if (!visual) return null;

  return (
    <DSBadge variant="neutral" size={size} leadingIcon={<DSIcon name={visual.icon} />}>
      {label || visual.label}
    </DSBadge>
  );
}

export default MembershipChip;
